// R-52 sign-in, Google only (spec build/specs/R-52-sign-in.md; Alvin 10/08: no Apple, no email link yet).
//
// One Google trip for everyone (Alvin 10/08: linking first made someone with an existing account sign in twice).
// Before leaving, while still the anonymous visitor, the app gets a merge ticket from the server; then it signs in with
// Google (signInWithOAuth). Back from Google it spends the ticket (account {merge}): the server moves every row the
// visitor owned onto the Google account and deletes the visitor. A brand-new Google account has no rows and no
// profile yet, so it simply takes the visitor's (home, interests, pace, plans); an account used before keeps its own
// home and combines the rest. The user id changes to the account's; nothing is lost.
//
// Between the return and the merge nothing else may run as the new account (Browse would create an empty profile and
// draft that then outrank the visitor's), so the app holds its screens behind the sign-in gate until the merge lands.
//
// The step in progress survives the Google redirect in localStorage. We always come back to the bare origin (it matches
// the redirect allow-list for production, previews and local dev) and then reload on the screen they left.
// Keep the default implicit flow: PKCE would break a sign-in that comes back in another browser (spec, Risks).
import { useSyncExternalStore } from 'react'
import * as Sentry from '@sentry/react'
import { ApiError, callFn, ensureSession, signinHold, supabase, takeAuthReturn, track } from './api'
import { forgetPlan } from './browse'

export type SignInFrom = 'e2' | 'share' | 'settings'
/** A plan that came over from this browser's anonymous visitor (enough to title it). */
export type MovedPlan = { id: string; title: string | null; dest?: { label?: string } | null; start_date?: string | null; end_date?: string | null }
type Pending = { step: 'signin'; from: string; back: string; via: SignInFrom; at: number; ticket: string; anon: { access_token: string; refresh_token: string }; refused?: boolean }

const PENDING = 'mg_signin_pending'
const E2_SHOWN = 'mg_signin_e2'
const SIGNIN_MAX_MS = 7 * 24 * 3600 * 1000

// ---------- who is signed in ----------
export type Who = { ready: boolean; id: string | null; anonymous: boolean; email: string | null }
let who: Who = { ready: false, id: null, anonymous: true, email: null }
const whoSubs = new Set<() => void>()
function setWho(next: Who) {
  who = next
  whoSubs.forEach((f) => f())
}
supabase.auth.onAuthStateChange((_e, session) => {
  const u = session?.user
  setWho({ ready: true, id: u?.id ?? null, anonymous: u ? Boolean(u.is_anonymous) : true, email: u?.email ?? null })
  // another tab of this browser came back from Google: this tab now holds the account session too, so it stops until
  // that tab's merge lands (then reloads, see the storage listener below)
  if (u && !u.is_anonymous && !gate && !merging && readPending()) {
    heldByOtherTab = true
    setGate(true)
    setStatus({ kind: 'working', slow: false })
  }
})
export function useWho(): Who {
  return useSyncExternalStore((f) => { whoSubs.add(f); return () => { whoSubs.delete(f) } }, () => who, () => who)
}

// ---------- what the host shows ----------
export type Status =
  | { kind: 'idle' }
  | { kind: 'prompt'; via: SignInFrom }                       // the sign-in sheet (from the share line's Sign in)
  | { kind: 'working'; slow: boolean }                        // back from Google, finishing
  | { kind: 'done' }                                          // signed in, same id, nothing moved
  | { kind: 'merged'; plans: MovedPlan[] }
  | { kind: 'failed' }
  | { kind: 'merge_failed' }
let status: Status = { kind: 'idle' }
const subs = new Set<() => void>()
function setStatus(s: Status) {
  status = s
  subs.forEach((f) => f())
}
export function useSignInStatus(): Status {
  return useSyncExternalStore((f) => { subs.add(f); return () => { subs.delete(f) } }, () => status, () => status)
}
export function closeSignIn(dismissedFrom?: SignInFrom) {
  if (dismissedFrom) track('signin_dismissed', { from: dismissedFrom })
  setStatus({ kind: 'idle' })
}
let lastVia: SignInFrom = 'settings'
export function openSignIn(via: SignInFrom) {
  lastVia = via
  setStatus({ kind: 'prompt', via })
}
/** Try again after a sign-in that did not finish: the same sheet they started from. */
export function reopenSignIn() {
  // the Saved card and the Settings row carry the choices in the page: go straight to Google, never the sheet
  if (lastVia === 'e2' || lastVia === 'settings') {
    setStatus({ kind: 'idle' })
    void startGoogle(lastVia).catch((e) => { Sentry.captureException(e); setStatus({ kind: 'failed' }) })
    return
  }
  openSignIn(lastVia)
}

/**
 * E2 (10/08 late, after the journey tests): not a sheet any more. A sheet that opens on Save swallows the person's next
 * tap (a second Save, the Saved tab, the day builder). After their first save in this browser, the Saved screen
 * carries a quiet in-page card instead, until they sign in or say Not now (browse.ts sends `mg:add` on every save).
 */
const E2_SAVED = 'mg_signin_saved'
if (typeof window !== 'undefined') window.addEventListener('mg:add', () => {
  try { if (!localStorage.getItem(E2_SAVED)) localStorage.setItem(E2_SAVED, String(Date.now())) } catch { /* no card */ }
})
/** Whether the Saved screen shows the sign-in card (read on render; Not now and signing in end it). */
export function showSavedCard(): boolean {
  if (!who.ready || !who.anonymous) return false
  try { return Boolean(localStorage.getItem(E2_SAVED)) && !localStorage.getItem(E2_SHOWN) } catch { return false }
}
export function dismissSavedCard() {
  try { localStorage.setItem(E2_SHOWN, String(Date.now())) } catch { /* shows again next time */ }
  track('signin_dismissed', { from: 'e2' })
}

/** Google refuses sign-in inside some apps' built-in browsers (embedded-browser policy). */
export function inAppBrowser(): boolean {
  return typeof navigator !== 'undefined' && /\b(Instagram|FBAN|FBAV|FB_IAB|TikTok|musical_ly|Bytedance|Snapchat|Line\/|LinkedInApp|Pinterest)\b/i.test(navigator.userAgent)
}

// The pending step lives in localStorage, not the tab: once the Google account has replaced the anonymous session, this
// record (ticket + the visitor's refresh token) is the only way back to the visitor's rows, so it is kept until the
// server confirms the merge, or the person says Not now on the failure sheet.
function readPending(): Pending | null {
  try {
    const p = JSON.parse(localStorage.getItem(PENDING) ?? 'null') as Pending | null
    if (!p) return null
    // a record from the old two-trip flow ('link') carries no ticket: nothing to finish
    if (p.step === 'signin' && Date.now() - p.at < SIGNIN_MAX_MS) return p
    localStorage.removeItem(PENDING)
    return null
  } catch {
    return null
  }
}
function writePending(p: Pending | null) {
  try {
    if (p) localStorage.setItem(PENDING, JSON.stringify(p))
    else localStorage.removeItem(PENDING)
  } catch {
    /* private mode: the redirect still works, the return just shows nothing */
  }
  if (!p) setGate(false)
}

// ---------- the gate: no screen runs as the new account before the merge ----------
let gate = false
let merging = false          // this tab is the one finishing the sign-in
let heldByOtherTab = false   // this tab stopped because another one is finishing it
const gateSubs = new Set<() => void>()
function setGate(on: boolean) {
  signinHold.on = on
  if (gate === on) return
  gate = on
  gateSubs.forEach((f) => f())
}
setGate(readPending() !== null)
if (typeof window !== 'undefined') {
  // a held tab starts over (and finishes the merge itself if the record is still there) when the record changes in
  // another tab (merged, let go, or a new ticket), or when the person comes back to it
  window.addEventListener('storage', (e) => { if (e.key === PENDING && heldByOtherTab) location.reload() })
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible' && heldByOtherTab) location.reload() })
}
/** True while the gate is on (a sign-in back from Google still has its merge to finish). */
export const signInGated = () => gate
/** True while a sign-in that came back from Google still has a merge to finish: App shows only the sign-in host. */
export function useSignInGate(): boolean {
  return useSyncExternalStore((f) => { gateSubs.add(f); return () => { gateSubs.delete(f) } }, () => gate, () => gate)
}
// what to show after the reload that follows a merge (the screen behind it then loads the merged data)
const SHOW = 'mg_signin_show'
function readShow(): Status | null {
  try {
    const v = JSON.parse(sessionStorage.getItem(SHOW) ?? 'null') as Status | null
    sessionStorage.removeItem(SHOW)
    return v && (v.kind === 'merged' || v.kind === 'done') ? v : null
  } catch {
    return null
  }
}
const here = () => location.pathname + location.search
const redirectTo = () => location.origin

/** Every sign-in starts here: one Google trip. False when there is nothing to do (already signed in). */
export async function startGoogle(via: SignInFrom): Promise<boolean> {
  const s = await ensureSession()
  if (!s.user.is_anonymous) return false
  lastVia = via
  track('signin_started', { from: via })
  await leaveForGoogle({ from: s.user.id, back: here(), via })
  return true
}

/** Ticket first (still the visitor), then Google. Also the Try again path after a refused ticket. */
async function leaveForGoogle(p: { from: string; back: string; via: SignInFrom }): Promise<void> {
  const { data } = await supabase.auth.getSession()
  const s = data.session
  if (!s || !s.user.is_anonymous || s.user.id !== p.from) throw new Error('anonymous session gone')
  const { ticket } = await callFn<{ ticket: string }>('account', { ticket: true })
  writePending({ step: 'signin', from: p.from, back: p.back, via: p.via, ticket, anon: { access_token: s.access_token, refresh_token: s.refresh_token }, at: Date.now() })
  // without the record the return could not merge and the visitor's plans would be stranded: stop here instead
  if (readPending()?.ticket !== ticket) throw new Error('cannot keep the sign-in step in this browser')
  const { error } = await supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: redirectTo() } })
  if (error) {
    writePending(null)
    throw error
  }
}

/**
 * Put the anonymous visitor back in this browser (its ticket was refused, so a fresh one is needed). Checked with the
 * server (getUser), because setSession trusts an unexpired access token without asking: a visitor already merged and
 * deleted would otherwise come back as a dead session.
 */
async function restoreAnon(p: Pending): Promise<'ok' | 'gone' | 'error'> {
  const { data, error } = await supabase.auth.setSession(p.anon)
  if (error || data.session?.user.id !== p.from) return error && /not.?found|invalid|expired|revoked/i.test(error.message) ? 'gone' : 'error'
  const check = await supabase.auth.getUser()
  if (check.error || check.data.user?.id !== p.from) return check.error && check.error.status && check.error.status < 500 ? 'gone' : 'error'
  return 'ok'
}
type Tokens = { access_token: string; refresh_token: string }
async function currentTokens(): Promise<Tokens | null> {
  const { data } = await supabase.auth.getSession()
  return data.session ? { access_token: data.session.access_token, refresh_token: data.session.refresh_token } : null
}

let navigateTo: ((to: string) => void) | null = null
function goBack(p: Pending) {
  if (p.back && p.back !== here()) navigateTo?.(p.back)
}

/**
 * Spend the ticket (the account session is in place). Success: the record goes, and the page reloads on the screen they
 * left so it shows the merged data, with the result sheet on top. Failure: the record stays; a refused ticket (409:
 * expired, used, or the visitor is gone) is marked so Try again starts over from the visitor.
 */
async function attemptMerge(p: Pending): Promise<void> {
  merging = true
  setStatus({ kind: 'working', slow: false })
  const slow = setTimeout(() => { if (status.kind === 'working') setStatus({ kind: 'working', slow: true }) }, 10_000)
  try {
    const { data } = await supabase.auth.getSession()
    const u = data.session?.user
    // a Google account made on this trip (its sign-up is newer than the ticket) had nothing of its own: say "came
    // with you", not "we kept the plans from both"
    const guess = Boolean(u?.created_at && Date.parse(u.created_at) >= p.at - 120_000)
    const res = await callFn<{ merged: boolean; plans: MovedPlan[]; fresh?: boolean }>('account', { merge: p.ticket })
    // the server says whether the account had anything of its own (once 20261008233000 is live); else the sign-up time
    const fresh = typeof res.fresh === 'boolean' ? res.fresh : guess
    track('signin_linked', { fresh, plans: res.plans.length })
    clearDeviceCaches()
    forgetPlan()
    try { sessionStorage.setItem(SHOW, JSON.stringify(!fresh && res.plans.length ? { kind: 'merged', plans: res.plans } : { kind: 'done' })) } catch { /* the sheet is skipped */ }
    try { localStorage.removeItem(PENDING) } catch { /* gone with the reload */ }
    location.replace(p.back || '/')
  } catch (e) {
    const refused = e instanceof ApiError && e.status === 409
    if (!refused) Sentry.captureException(e)
    track('signin_failed', { code: e instanceof ApiError ? `merge_${e.status}` : 'merge', step: 'merge' })
    // another tab may have spent this ticket and cleared the record meanwhile: never bring it back
    if (readPending()?.ticket === p.ticket) writePending({ ...p, refused })
    if (readPending()) setStatus({ kind: 'merge_failed' })
    else location.replace(p.back || '/') // another tab finished it: start over with the merged state
  } finally {
    clearTimeout(slow)
  }
}

/**
 * Try again from merge_failed: the same ticket first. A refused one (409) means back to the visitor, a new ticket and
 * Google again; the account session is kept aside and put back if any step of that fails, so nobody is signed out of
 * their account without asking.
 */
export async function retryMerge(): Promise<void> {
  const p = readPending()
  if (!p) return setStatus({ kind: 'failed' })
  if (!p.refused) return attemptMerge(p)
  setStatus({ kind: 'working', slow: false })
  const account = await currentTokens()
  const putBack = async () => { if (account) await supabase.auth.setSession(account).catch(() => null) }
  const r = await restoreAnon(p)
  if (r !== 'ok') {
    await putBack()
    if (r === 'gone') {
      // the visitor no longer exists: an earlier merge landed (its answer was lost), or it was cleaned up long ago
      track('signin_failed', { code: 'visitor_gone', step: 'merge' })
      writePending(null)
      setStatus({ kind: 'done' })
    } else {
      track('signin_failed', { code: 'restore', step: 'merge' })
      setStatus({ kind: 'merge_failed' })
    }
    return
  }
  try {
    await leaveForGoogle(p)
  } catch (e) {
    Sentry.captureException(e)
    await putBack()
    setStatus({ kind: 'merge_failed' })
  }
}

/** Not now on merge_failed: the person chose to leave this phone's plans behind. */
export function dismissMergeFailed() {
  writePending(null)
  closeSignIn()
}

/** Called once by the host on every page load: finishes whatever a Google redirect left half done. */
export async function resumeSignIn(nav: (to: string) => void): Promise<void> {
  navigateTo = nav
  const ret = takeAuthReturn()
  const shown = readShow()
  if (shown) setStatus(shown)
  const p = readPending()
  if (!p) return setGate(false)
  lastVia = p.via
  const { data } = await supabase.auth.getSession()
  const u = data.session?.user
  try {
    // step 'signin'
    if (!u) {
      // no session at all (an expired one was dropped): bring the visitor back if it still exists, else nothing to do
      writePending(null)
      if ((await restoreAnon(p)) !== 'ok') await supabase.auth.signOut({ scope: 'local' }).catch(() => null)
      return
    }
    if (u.is_anonymous) {
      // a tab opened while another one is still at Google: leave that tab's record alone
      if (!ret && Date.now() - p.at < 20 * 60 * 1000) return setGate(false)
      // still the visitor: the Google sign-in failed or was abandoned, and nothing changed
      writePending(null)
      if (ret) {
        track('signin_failed', { code: ret.error ?? 'still_anonymous', step: 'signin' })
        setStatus({ kind: 'failed' })
        goBack(p)
      }
      return
    }
    // signed in to the account: spend the ticket (on this load, or a later one until it lands or they let it go).
    // A ticket already refused waits for an explicit Try again instead of being re-sent on every load.
    if (p.refused) return setStatus({ kind: 'merge_failed' })
    await attemptMerge(p)
  } catch (e) {
    Sentry.captureException(e)
    // with a record still waiting the app is held: offer Try again / Not now, never a dead end
    setStatus(readPending() ? { kind: 'merge_failed' } : { kind: 'failed' })
  }
}

/** A merge changed which plan is the draft and what the device remembered about the old visitor. */
function clearDeviceCaches() {
  try {
    sessionStorage.removeItem('mg_picks')
    for (const k of Object.keys(sessionStorage)) if (k.startsWith('mg_browse:')) sessionStorage.removeItem(k)
    // the first-run cache is the visitor's: after the merge the account's (combined) answers come from the server
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith('mg_plan') || k === 'mg_browse_plan' || k === 'mg_who' || k === 'mg_first_run' || k === 'mg_first_run_done') localStorage.removeItem(k)
    }
  } catch {
    /* nothing to clear */
  }
}

/**
 * Sign out of this browser only (other devices stay signed in), and leave nothing of the person here: the session, every
 * stored key and cache. The review key (the founder's own tool, not the person's data) is kept. Then a full reload,
 * so the next screen is the first run of a new anonymous visitor.
 */
export async function signOutHere(): Promise<void> {
  track('signed_out', {})
  const { error } = await supabase.auth.signOut({ scope: 'local' })
  if (error) Sentry.captureException(error)
  wipeBrowser()
  location.replace('/')
}

export function wipeBrowser() {
  try {
    const review = localStorage.getItem('magellan-review')
    localStorage.clear()
    if (review) localStorage.setItem('magellan-review', review)
  } catch { /* storage blocked */ }
  try { sessionStorage.clear() } catch { /* storage blocked */ }
  if (typeof caches !== 'undefined') void caches.keys().then((ks) => Promise.all(ks.map((k) => caches.delete(k)))).catch(() => {})
}

// Dev server only (stripped from builds): lets a QA walk show each state without a Google round trip.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  ;(window as unknown as { __signin: unknown }).__signin = { setStatus, setWho: (w: Partial<Who>) => setWho({ ...who, ...w }) }
}
